import useInfiniteScroll from "react-infinite-scroll-hook";
import Loader from "../AnimatedLoader";
import { useTranslations } from "use-intl";
import PageLoader from "../PageLoader";
import { formatCurrency } from "@/app/lib/utils/currency";
import { ReservationType } from "@/app/lib/types/enums";
import { BookedHotelCard } from "../hotels-list/BookedHotelCard";
import { useReservations } from "@/app/lib/hooks/useReservations";
import { OrderCardWithoutReservation } from "../OrderCardWithoutReservation";
import { OrdersInCreationCard } from "./OrdersInCreationCard";

export const MyHotelsList = ({
  onReservationClick,
  reservationType,
  reservationTypeSwitcher,
}: {
  onReservationClick: (reservationId: string) => void;
  reservationType: ReservationType;
  reservationTypeSwitcher: React.ReactNode;
}) => {
  const t = useTranslations();
  const { data, isLoading, isFetching, isError, hasReachedEnd, fetchNextPage } =
    useReservations({ searchFilter: reservationType, service: "hotel" });
  const [sentryRef] = useInfiniteScroll({
    loading: isFetching,
    hasNextPage: !hasReachedEnd,
    onLoadMore: () => {
      fetchNextPage();
    },
    disabled: isError,
    rootMargin: "0px 0px 40px 0px",
  });

  if (isLoading) {
    return (
      <PageLoader>
        <Loader icon="buildings" />
      </PageLoader>
    );
  }

  const bookingCount = data?.pages?.[0]?.data?.count;
  const bookings =
    data?.pages?.flatMap((page) => page.data.hotelReservations) || [];
  const inProgressReservationsCount =
    data?.pages?.[0]?.data?.inProgressReservationsCount;

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        {reservationTypeSwitcher}
        {bookingCount ? (
          <div className="text-md font-medium">
            {t("hotels.bookedHotels", { bookingCount })}
          </div>
        ) : null}
      </div>

      {inProgressReservationsCount && inProgressReservationsCount > 0 ? (
        <OrdersInCreationCard count={inProgressReservationsCount} />
      ) : null}

      {bookings.length > 0 ? (
        <ul aria-label={t("hotels.bookedHotelsList")} className="space-y-2">
          {bookings.map((reservation) => (
            <li key={reservation.id}>
              {reservation.status === ReservationType.REFUNDED ? (
                <OrderCardWithoutReservation
                  reservation={reservation}
                  onClick={() => onReservationClick(reservation.id)}
                />
              ) : (
                <BookedHotelCard
                  reservation={reservation.externalReservation}
                  onClick={() => onReservationClick(reservation.id)}
                  footer={
                    <div className="text-end text-sm">
                      <span className="font-semibold text-text-primary">
                        {formatCurrency(
                          reservation.price,
                          reservation.currency,
                        )}
                      </span>
                    </div>
                  }
                />
              )}
            </li>
          ))}
        </ul>
      ) : null}

      {isFetching || !hasReachedEnd ? (
        <div
          className="flex items-center justify-center py-4"
          ref={sentryRef}
          aria-hidden={!isFetching}
        >
          <Loader icon="buildings" />
        </div>
      ) : (
        <div className="flex items-center justify-center p-8 text-text-tertiary">
          {t("hotels.noMoreResults")}
        </div>
      )}
    </>
  );
};
